<template>
    <x-container fluid pa-0>
        <x-layout>
            <x-flex xs12>
                <summary-tabs-buttons
                    v-model="activeTab"
                    :tabs="summaryTabs"
                />
            </x-flex>
        </x-layout>

        <template v-if="activeTab === 'firm_order'">
            <x-layout v-if="isCaptiveReinsurance">
                <x-flex xs12 class="right">
                    <x-card class="panel mb-4">
                        <x-card-title>
                            <h3>Captive offer</h3>
                        </x-card-title>

                        <x-card-text>
                            <x-data-table
                                v-if="captiveInsurerOfferIsOffered"
                                class="captive-offer-table no-row-hover"
                                :headers="offeredTableHeaders"
                                :items="[captiveInsurerOffer]"
                                hide-actions
                            >
                                <template v-slot:items="props">
                                    <tr>
                                        <td class="included-column text-xs-center">
                                            <x-checkbox
                                                :input-value="props.item.is_selected"
                                                hide-details
                                                readonly
                                            />
                                        </td>

                                        <td class="insurer-column">
                                            <span class="insurer-name">{{ props.item.captive_insurer.name }}</span>
                                        </td>

                                        <td>
                                            <progress-linear-bar
                                                v-model="props.item.line_size_percent"
                                                :label="$options.filters.percent(props.item.line_size_percent)"
                                                :end-label="$options.filters.currency(props.item.line_size_amount, submission.currency.code)"
                                                color="teal"
                                                small
                                            />
                                        </td>

                                        <td class="text-xs-right">
                                            N/A

                                            <div v-if="showHorizontalPriceChange(0)">
                                                <x-icon size="18">arrow_upward</x-icon>
                                                {{ pricing.horizontalPrice }} BPS
                                            </div>

                                            <div v-if="showHorizontalPercentageChange(null, true)">
                                                <x-icon size="18">arrow_upward</x-icon>
                                                {{ pricing.horizontalPrice }} BPS
                                            </div>
                                        </td>

                                        <td>
                                            {{ getOfferTypeName(props.item.type) }}
                                        </td>

                                        <td>
                                            <value-percent-split-input
                                                v-model="props.item.written_line_percent"
                                                :total="lineRequired"
                                                :maxAmount="props.item.line_size_amount"
                                                :currency-code="submission.currency.code"
                                                :required="props.item.is_selected"
                                                :allowZero="true"
                                                hide-details
                                                no-margin
                                                small
                                            />
                                        </td>

                                        <td>
                                            N/A
                                        </td>

                                        <td class="text-xs-center" :class="{ 'no-padding': !props.item.subjectivities.length }">
                                            <warning-items
                                                v-if="props.item.subjectivities.length > 0"
                                                v-model="props.item.subjectivities"
                                                item-text="name"
                                                item-width="200"
                                            />
                                        </td>

                                        <td />
                                    </tr>
                                </template>
                            </x-data-table>

                            <x-data-table
                                v-else
                                class="captive-requested-offer-table no-row-hover"
                                :headers="nonOfferedTableHeaders"
                                :items="[captiveInsurerOffer]"
                                hide-actions
                            >
                                <template v-slot:no-data>
                                    <p class="error-message text-xs-center">No outstanding offers found</p>
                                </template>

                                <template v-slot:items="props">
                                    <tr>
                                        <td class="insurer-column">
                                            <span class="insurer-name">{{ props.item.captive_insurer.name }}</span>
                                        </td>

                                        <td>
                                            <status-chip :status="props.item.status" small class="ma-0" />
                                        </td>

                                        <td>
                                            {{ getOfferComment(props.item) }}
                                        </td>

                                        <td />
                                    </tr>
                                </template>
                            </x-data-table>
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12 class="right">
                    <x-card class="panel mb-4">
                        <x-card-title>
                            <h3>Received offers ({{ insurerOfferedOffers?.length || 0 }})</h3>
                        </x-card-title>

                        <x-card-text>
                            <x-form v-model="isFirmOrderValid">
                                <x-data-table
                                    :headers="offeredTableHeaders"
                                    :items="insurerOfferedOffers"
                                    class="offered-offers-table no-row-hover"
                                    hide-actions
                                >
                                    <template v-slot:no-data>
                                        <p class="error-message text-xs-center">No offers found</p>
                                    </template>

                                    <template v-slot:items="props">
                                        <tr>
                                            <td class="included-column text-xs-center">
                                                <span>
                                                    <x-checkbox
                                                        :input-value="props.item.is_selected"
                                                        :disabled="offerIsDisabled(props.item)"
                                                        hide-details
                                                        @click.stop="checkInclude(props.item)"
                                                    />
                                                </span>
                                            </td>

                                            <td class="insurer-column">
                                                <span class="insurer-name">{{ props.item.insurer.name }}</span>

                                                <div class="sub-text">
                                                    Brokerage: {{ props.item.brokerage | percent }}

                                                    <i
                                                        class="material-icons"
                                                        :style="{ visibility: showEditBrokerage ? 'visible' : 'hidden' }"
                                                        @click="editBrokerage(props.item)"
                                                    >edit</i>
                                                </div>
                                            </td>

                                            <td>
                                                <progress-linear-bar
                                                    v-model="props.item.adjusted_line_size_percent"
                                                    :label="$options.filters.percent(props.item.adjusted_line_size_percent)"
                                                    :end-label="$options.filters.currency(props.item.adjusted_line_size_amount, submission.currency.code)"
                                                    color="teal"
                                                    small
                                                />
                                            </td>

                                            <td v-if="isRateOffer(props.item.type)" class="text-xs-right">
                                                {{ props.item.price | numberNoTrail }} BPS

                                                <div v-if="showHorizontalPriceChange(props.item.price)">
                                                    <x-icon size="18">arrow_upward</x-icon>

                                                    {{ pricing.horizontalPrice }} BPS
                                                </div>
                                            </td>
                                            <td v-else-if="isFlatFeeOffer(props.item.type)" class="text-xs-right">
                                                {{ props.item.flat_fee | currency(submission.currency.code) }}
                                            </td>
                                            <td v-else-if="isPercentageOfMarginOffer(props.item.type)" class="text-xs-right">
                                                {{ getPercentageOfMarginOffer(props.item) }}

                                                <div v-if="showHorizontalPercentageChange(props.item)">
                                                    <x-icon size="18">arrow_upward</x-icon>
                                                    {{ pricing.horizontalPrice | numberNoTrail }} BPS
                                                </div>
                                            </td>
                                            <td v-else-if="isRetainedPercentOffer(props.item.type)" class="text-xs-right">
                                                N/A
                                            </td>

                                            <td>
                                                {{ getOfferTypeName(props.item.type) }}
                                            </td>

                                            <td>
                                                <value-percent-split-input
                                                    v-model="props.item.adjusted_written_line_percent"
                                                    :total="insurerLineRequired"
                                                    :maxAmount="props.item.adjusted_line_size_amount"
                                                    :currency-code="submission.currency.code"
                                                    :required="props.item.is_selected"
                                                    hide-details
                                                    no-margin
                                                    small
                                                />
                                            </td>

                                            <td v-if="isRetainedPercentOffer(props.item.type)">
                                                N/A
                                            </td>

                                            <td v-else>
                                                {{ props.item.waiting_period }} days
                                            </td>

                                            <td class="text-xs-center" :class="{ 'no-padding': !props.item.subjectivities.length }">
                                                <warning-items
                                                    v-if="props.item.subjectivities.length > 0"
                                                    v-model="props.item.subjectivities"
                                                    item-text="name"
                                                    item-width="200"
                                                />
                                            </td>

                                            <td class="text-xs-center">
                                                <x-btn
                                                    v-if="isValidRemove(props.item)"
                                                    class="square-icon extra-small"
                                                    color="tertiary"
                                                    @click="removeOffer(props.item)"
                                                >
                                                    <i class="material-icons">close</i>
                                                </x-btn>
                                            </td>
                                        </tr>
                                        <tr
                                            v-if="props.item.is_selected && getInsurerSecurities(props.item).length > 1"
                                            class="unstyled-row"
                                        >
                                            <td />

                                            <td colspan="8">
                                                <x-layout
                                                    v-for="(line, lineIndex) in offerLines(props.item)"
                                                    :key="`security-line-${props.item.id}-${lineIndex}`"
                                                    align-end
                                                >
                                                    <x-flex xs4>
                                                        <x-select
                                                            data-test="security_line"
                                                            :value="line.security_id"
                                                            :items="getSecuritiesFormatted(lineSecurityOptions(props.item, lineIndex))"
                                                            class="required"
                                                            label="Security"
                                                            placeholder="Security"
                                                            append-icon="keyboard_arrow_down"
                                                            :menu-props="{ offsetY: true }"
                                                            hide-details
                                                            @change="value => setLineSecurity(props.item, lineIndex, value)"
                                                        />
                                                    </x-flex>

                                                    <x-flex v-if="offerLines(props.item).length > 1" xs2 ml-3>
                                                        <x-text-field
                                                            :value="line.written_line_percent"
                                                            type="number"
                                                            min="0"
                                                            class="security-line-percent"
                                                            label="Written line"
                                                            suffix="%"
                                                            hide-details
                                                            :rules="[v => lineWrittenPercentRule(v)]"
                                                            @input="value => setLineWrittenPercent(props.item, lineIndex, value)"
                                                        />
                                                    </x-flex>

                                                    <x-flex
                                                        v-if="offerLines(props.item).length > 1 && isFlatFeeOffer(props.item.type)"
                                                        shrink
                                                        ml-3
                                                        mb-1
                                                        class="security-line-flat-fee"
                                                    >
                                                        {{ lineFlatFee(props.item, line) | currency(submission.currency.code) }}
                                                    </x-flex>

                                                    <x-flex v-if="offerLines(props.item).length > 1" shrink ml-3 mb-1>
                                                        <x-btn
                                                            class="square-icon extra-small ma-0"
                                                            color="tertiary"
                                                            @click="removeSecurityLine(props.item, lineIndex)"
                                                        >
                                                            <i class="material-icons">close</i>
                                                        </x-btn>
                                                    </x-flex>
                                                </x-layout>

                                                <p
                                                    v-if="offerLines(props.item).length > 1 && !offerLinesSplitValid(props.item)"
                                                    class="error-message security-line-error"
                                                >
                                                    Line percentages must each be above zero and total {{ props.item.adjusted_written_line_percent || props.item.written_line_percent }}%.
                                                </p>

                                                <x-btn
                                                    v-if="canAddSecurityLine(props.item)"
                                                    class="ma-0 mt-1"
                                                    color="primary"
                                                    outline
                                                    small
                                                    @click="addSecurityLine(props.item)"
                                                >
                                                    Add security line
                                                </x-btn>
                                            </td>
                                        </tr>

                                        <tr v-if="props.index === insurerOfferedOffers.length - 1" class="unstyled-row">
                                            <td colspan="5" />

                                            <td>
                                                <x-btn
                                                    class="sign-down-button"
                                                    color="secondary"
                                                    small
                                                    block
                                                    :disabled="totalSelectedPercentageOffered() < 100"
                                                    @click="signDownFirmOrder()"
                                                >
                                                    Sign down
                                                </x-btn>
                                            </td>
                                        </tr>
                                    </template>
                                </x-data-table>

                                <x-container pa-0>
                                    <x-layout>
                                        <x-flex xs4>
                                            <x-text-field
                                                v-if="netMarginRequired"
                                                v-model="localPricing.net_margin"
                                                class="required"
                                                label="Net margin"
                                                suffix="BPS"
                                                :rules="[
                                                    v => ruleRequired(v, 'Net margin'),
                                                    v => ruleNumeric(v, 'Net margin'),
                                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Net margin')
                                                ]"
                                            />
                                            <x-text-field
                                                v-if="grossMarginRequired"
                                                v-model="localPricing.gross_margin"
                                                class="required"
                                                label="Gross margin"
                                                suffix="BPS"
                                                :rules="[
                                                    v => ruleRequired(v, 'Gross margin'),
                                                    v => ruleNumeric(v, 'Gross margin'),
                                                    v => ruleGreaterThanOrEqualTo(v, 0, 'Gross margin')
                                                ]"
                                            />
                                        </x-flex>
                                    </x-layout>
                                </x-container>
                                </x-form>
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>

            <x-layout v-if="isCaptiveReinsurance">
                <x-flex xs4 class="left">
                    <x-card class="panel mb-5">
                        <x-card-title>
                            <h3>Firm order builder</h3>
                        </x-card-title>

                        <x-card-text>
                            <data-list v-model="captiveFirmOrderDataList" />
                        </x-card-text>
                    </x-card>
                </x-flex>

                <x-flex xs8>
                    <x-card class="panel mb-5">
                        <x-card-text>
                            <progress-linear-bar
                                v-model="percentageRetained"
                                class="mb-4"
                                label="Captive"
                                end-label="Reinsured"
                                :bar-label="captiveInsurerOfferIsOffered ? undefined : 'PENDING'"
                                bar-label-class="grey--text text--lighten-2"
                                color="teal"
                                :background-color="captiveInsurerOfferIsOffered ? 'teal' : undefined"
                                :background-opacity="captiveInsurerOfferIsOffered ? 0.3 : undefined"
                            />

                            <progress-linear-bar
                                v-model="percentageWritten"
                                class="mb-4"
                                label="Written"
                                :end-label="$options.filters.percent(percentageWritten)"
                                :end-label-color="getEndLabelColor(percentageWritten)"
                                color="teal"
                            />

                            <div class="text-xs-right">
                                <x-btn
                                    color="primary"
                                    large
                                    :disabled="!isFacilityActive() || !isLineWrittenFull || !selectedOffersAreValid || netMarginNotProvided || grossMarginNotProvided || !captiveInsuranceValid || isClosed"
                                    @click="goToNextStep"
                                >
                                    Next
                                </x-btn>
                            </div>
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>

            <x-layout v-else>
                <x-flex xs4 class="left">
                    <x-card class="panel mb-5">
                        <x-card-title>
                            <h3>Firm order builder</h3>
                        </x-card-title>

                        <x-card-text>
                            <data-list v-model="insurerFirmOrderDataList" />
                        </x-card-text>
                    </x-card>
                </x-flex>

                <x-flex xs8>
                    <x-card class="panel mb-5">
                        <x-card-text>
                            <x-switch
                                v-model="localPricing.structure"
                                class="double-switch mt-0 mb-4"
                                label="Horizontal"
                                true-value="vertical"
                                false-value="horizontal"
                                color="primary"
                                :disabled="isCaptiveReinsurance"
                                hide-details
                                @change="switched"
                            >
                                <template v-slot:append>Vertical</template>
                            </x-switch>

                            <progress-linear-bar
                                v-model="insurerPercentageOffered"
                                class="mb-4"
                                label="Offered"
                                :end-label="$options.filters.percent(insurerPercentageOffered)"
                                :end-label-color="getEndLabelColor(insurerPercentageOffered)"
                                color="teal"
                            />

                            <progress-linear-bar
                                v-model="insurerPercentageWritten"
                                class="mb-4"
                                label="Written"
                                :end-label="$options.filters.percent(insurerPercentageWritten)"
                                :end-label-color="getEndLabelColor(insurerPercentageWritten)"
                                color="teal"
                            />

                            <div class="text-xs-right">
                                <x-btn
                                    color="primary"
                                    large
                                    :disabled="!isFacilityActive() || !isLineWrittenFull || !selectedOffersAreValid || netMarginNotProvided || grossMarginNotProvided || !captiveInsuranceValid || isClosed"
                                    @click="goToNextStep"
                                >
                                    Next
                                </x-btn>
                            </div>

                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12 class="right">
                    <x-card class="panel">
                        <x-card-title class="justify-space-between align-end">
                            <h3>Requested to offer ({{ insurerNonOfferedOffers.length }})</h3>

                            <x-btn
                                v-if="isInTheMarket && filteredAddInsuresList.length > 0"
                                color="secondary"
                                @click="openAddInsurersModal"
                            >
                                <i class="material-icons mr-2">group_add</i>

                                Add more insurers
                            </x-btn>
                        </x-card-title>

                        <x-card-text>
                            <x-data-table
                                class="requested-offers-table no-row-hover"
                                :headers="nonOfferedTableHeaders"
                                :items="insurerNonOfferedOffers"
                                hide-actions
                            >
                                <template v-slot:no-data>
                                    <p class="error-message text-xs-center">No outstanding offers found</p>
                                </template>

                                <template v-slot:items="props">
                                    <tr v-if="Boolean(props.item.insurer_company_id)">
                                        <td class="insurer-column">
                                            <span class="insurer-name">{{ props.item.insurer.name }}</span>

                                            <div class="sub-text">
                                                Brokerage: {{ props.item.brokerage | percent }}

                                                <i
                                                    class="material-icons"
                                                    :style="{ visibility: showEditBrokerage ? 'visible' : 'hidden' }"
                                                    @click="editBrokerage(props.item)"
                                                >edit</i>
                                            </div>
                                        </td>

                                        <td>
                                            <status-chip :status="props.item.status" small class="ma-0" />
                                        </td>

                                        <td>
                                            {{ getOfferComment(props.item) }}
                                        </td>

                                        <td class="text-xs-center">
                                            <x-btn
                                                v-if="isValidRemove(props.item)"
                                                class="square-icon extra-small"
                                                color="tertiary"
                                                @click="removeOffer(props.item)"
                                            >
                                                <i class="material-icons">close</i>
                                            </x-btn>
                                        </td>
                                    </tr>
                                </template>
                            </x-data-table>
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>
        </template>

        <template v-if="activeTab === 'product'">
            <x-layout>
                <x-flex xs6 class="left">
                    <x-card class="panel">
                        <x-card-title>
                            <h3>Submission information</h3>
                        </x-card-title>

                        <x-card-text>
                            <summary-data-list v-model="submission" />
                        </x-card-text>
                    </x-card>
                </x-flex>

                <x-flex xs6 class="right">
                    <x-card v-if="hasObligor" class="panel mb-5">
                        <x-card-title>
                            <h3>Obligors</h3>
                        </x-card-title>

                        <x-card-text>
                            <obligor-data-list v-model="submission.obligors" obligor :currency-code="submission.currency.code" />
                        </x-card-text>
                    </x-card>

                    <x-card v-if="hasObligor && submission.guarantors.length > 0" class="panel">
                        <x-card-title>
                            <h3>Guarantors</h3>
                        </x-card-title>

                        <x-card-text>
                            <obligor-data-list v-model="submission.guarantors" />
                        </x-card-text>
                    </x-card>
                </x-flex>
            </x-layout>
        </template>

        <template v-if="activeTab === 'additional_information'">
            <x-layout>
                <x-flex>
                    <additional-information-tab
                        v-model="submission.information_sections"
                        :insurer-list="insurerList"
                    />
                </x-flex>
            </x-layout>
        </template>

        <template v-if="activeTab === 'market_response_sheet'">
            <x-layout>
                <x-flex>
                    <market-response-sheet-tab :risk="submission" :can-download="canDownloadMarketSheet" />
                </x-flex>
            </x-layout>
        </template>

        <template v-if="activeTab === 'documents'">
            <x-layout>
                <x-flex xs12>
                    <document-list :insurer-list="insurerList" insurer-specific-upload upload :facility-id="submission.facility_id" :documentable-id="submission.risk_id" documentable-type="submission" :is-standalone-policy="submission.risk_structure === 'standalone_policy'" />
                </x-flex>
            </x-layout>
        </template>

        <modal
            ref="signDown"
            title="Sign down firm order"
            confirmation
        >
            <p>Are you sure you want to sign down this firm order?</p>
        </modal>

        <modal
            ref="closeSubmission"
            title="Close submission"
            confirmation
        >
            <p>
                Are you sure you want to close this Enquiry for
                {{submission.client_company.name}}/{{submission.obligor_name}}, this will also close all underwriter
                offers?
            </p>
        </modal>

        <modal
            ref="reopenSubmission"
            title="Reopen submission"
            confirmation
        >
            <p>
                Are you sure you want to reopen this Enquiry for
                {{submission.client_company.name}}/{{submission.obligor_name}}?
            </p>
        </modal>

        <modal
            ref="confirmRemoveOffer"
            title="Remove insurer"
            actionText="Remove"
            confirmation
        >
            <p>
                Are you sure you want to remove {{removingOffer ? removingOffer.insurer.name : ''}}?
            </p>
        </modal>

        <x-dialog
            v-if="showRemoveInsurerDialog"
            v-model="showRemoveInsurerDialog"
            width="600"
        >
            <x-form ref="removeInsurerForm" v-model="removeInsurerFormValid">
                <x-container fluid>
                    <x-layout>
                        <x-flex xs12>
                            <h2>Remove insurer - {{removingOffer ? removingOffer.insurer.name : ''}}</h2>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12>
                            <x-textarea
                                v-model="removeInsurerReason"
                                label="Reason"
                                placeholder=" "
                                :rules="[v => ruleRequired(v, 'Reason')]"
                                outline
                                maxlength="500"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout class="response-actions" justify-space-between>
                        <x-flex xs6 class="text-xs-left">
                            <x-btn
                                @click="showRemoveInsurerDialog=false"
                                class="ma-0"
                                color="tertiary"
                                small
                            >
                                Cancel
                            </x-btn>
                        </x-flex>

                        <x-flex xs6 class="text-xs-right">
                            <x-btn
                                @click="removeOfferForm"
                                class="ma-0"
                                color="primary"
                                small
                                :disabled="!removeInsurerFormValid"
                                :loading="removeInsurerLoading"
                            >
                                Remove
                            </x-btn>
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-form>
        </x-dialog>

        <modal
            ref="editBrokerage"
            title="Edit Brokerage"
            confirmation
            actionText="Save"
            :actionDisabled="!editBrokerageFormIsValid"
        >
            <x-form ref="editBrokerageForm" v-model="editBrokerageFormIsValid">
                <x-text-field
                    v-model="brokerage"
                    label="Brokerage"
                    placeholder=" "
                    :rules="[
                        v => ruleRequired(v, 'Brokerage'),
                        v => ruleGreaterThanOrEqualTo(v, 0, 'Brokerage'),
                        v => ruleLessThanOrEqualTo(v, 100, 'Brokerage')
                    ]"
                    suffix="%"
                    outline
                    @keypress.enter.prevent
                />
            </x-form>
        </modal>

        <modal
            ref="addInsurersModal"
            title="Add Insurers"
            actionText="Add"
            confirmation
            :actionDisabled="!selectedInsurersList.length"
        >
            <x-flex xs12>
                <x-container>
                    <template>
                        <add-insurers
                            :selectedInsurersUsers.sync="selectedInsurersUsers"
                            :insurers="filteredAddInsuresList"
                        />
                    </template>
                </x-container>
            </x-flex>
        </modal>

        <modal
            ref="addInsurersConfirmModal"
            title="Confirm: Add Insurers"
            actionText="Confirm"
            confirmation
        >
            <p><strong>Please confirm that you wish to add the following insurers:</strong></p>

            <ul>
                <li v-for="insurer in selectedInsurersList">{{ insurer.name }}</li>
            </ul>
        </modal>
    </x-container>
</template>

<script>
    import SummaryTabsButtons from '~/components/Shared/UIComponents/SummaryTabsButtons';
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';
    import ProgressLinearBar from '~/components/Broker/UIComponents/ProgressLinearBar';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import DocumentList from '~/components/Shared/UIComponents/DocumentList';
    import SummaryDataList from '~/components/Shared/UIComponents/SummaryDataList';
    import DataList from '~/components/Shared/UIComponents/DataList';
    import ValuePercentSplitInput from '~/components/Shared/UIComponents/ValuePercentSplitInput';
    import CheckIcon from '~/components/Shared/UIComponents/CheckIcon';
    import WarningItems from '~/components/Shared/UIComponents/WarningItems';
    import AdditionalInformationTab from '~/components/Shared/UIComponents/AdditionalInformationTab';
    import AdditionalInformationDisplay from '~/components/Shared/UIComponents/AdditionalInformationDisplay';
    import Big from 'big.js';
    import premium from '~/mixins/premium';
    import rules from '~/mixins/rules';
    import ObligorDataList from "~/components/Shared/UIComponents/ObligorDataList";
    import AddInsurers from "../UIComponents/AddInsurers";
    import MarketResponseSheetTab from '~/components/Broker/UIComponents/MarketResponseSheetTab';
    import FacilityInactiveAlert from '~/components/Broker/Facility/InactiveAlert';
    import submissionHelpers from "../../../mixins/submissionHelpers";

    export default {
        name: 'FirmOrderBuilder',
        components: {
            SummaryTabsButtons,
            ProgressLinearBar,
            StatusChip,
            Modal,
            DocumentList,
            SummaryDataList,
            DataList,
            ValuePercentSplitInput,
            CheckIcon,
            WarningItems,
            AdditionalInformationTab,
            AdditionalInformationDisplay,
            ObligorDataList,
            AddInsurers,
            MarketResponseSheetTab,
            FacilityInactiveAlert
        },
        mixins: [rules, submissionHelpers],
        props: {
            submission: Object,
            currentStep: Number,
            pricing: Object,
            draftFirmOrder: Object
        },
        data() {
            return {
                activeTab: 'firm_order',
                offeredTablePagination: {
                    sortBy: 'insurer.name',
                },
                offeredTableHeaders: [
                    {
                        text: 'Include?',
                        value: 'is_selected',
                        align: 'center',
                        width: '5%',
                        sortable: false,
                    },
                    {
                        text: 'Insurer',
                        value: 'insurer.name',
                        width: '15%',
                    },
                    {
                        text: '% Offered',
                        value: 'line_size_percent',
                        width: '15%',
                    },
                    {
                        text: 'Price Offered',
                        value: 'price',
                        align: 'right',
                        width: '10%',
                    },
                    {
                        text: 'Offer Type',
                        value: 'type',
                        width: '10%',
                    },
                    {
                        text: 'Written Line',
                        value: '',
                        sortable: false,
                        width: '25%',
                    },
                    {
                        text: 'Waiting Period',
                        value: 'waiting_period',
                        width: '10%',
                    },
                    {
                        // warning items
                        text: '',
                        sortable: false,
                        class: 'no-padding',
                        align: 'center',
                        width: '5%',
                    },
                    {
                        text: 'Remove',
                        sortable: false,
                        align: 'center',
                        width: '5%',
                    },
                ],
                nonOfferedTableHeaders: [
                    {
                        text: 'Insurer',
                        value: 'insurer.name',
                        width: '15%',
                    },
                    {
                        text: 'status',
                        value: 'status',
                        width: '10%',
                    },
                    {
                        text: 'Feedback',
                        value: 'query',
                        width: '70%',
                    },
                    {
                        text: 'Remove',
                        sortable: false,
                        align: 'center',
                        width: '5%',
                    },
                ],
                localPricing: this.pricing,
                openFirmOrderPanelIndex: 0,
                closing: false,
                reopening: false,
                brokerage: null,
                editingBrokerage: false,
                editBrokerageFormIsValid: false,
                removingOffer: null,
                insurers: [],
                selectedInsurersUsers: {},
                removeInsurerFormValid: true,
                showRemoveInsurerDialog: false,
                removeInsurerLoading: false,
                removeInsurerReason: null,
                percentageDecimalPlaces: 12,
                isFirmOrderValid: false,
            }
        },
        computed: {
            actionMenuItems() {
                if (this.isClosed) {
                    return [
                        {
                            name: 'Reopen',
                            eventName: 'action-reopen-risk'
                        }
                    ];
                }
                    return [
                        {
                            name: 'Edit',
                            eventName: 'action-edit-risk'
                        },
                        {
                            name: 'Close risk',
                            eventName: 'action-close-risk'
                        },
                        {
                            name: 'Create a copy',
                            eventName: 'action-create-a-copy'
                        }
                    ];

            },
            summaryTabs() {
                const tabs = [
                    {
                        value: 'firm_order',
                        text: 'Firm Order',
                        count: this.offeredOffers.length
                    },
                    {
                        value: 'product',
                        text: 'Product',
                    },
                ];

                if (this.submission.information_sections.length > 0) {
                    tabs.push({
                        value: 'additional_information',
                        text: 'Additional Information',
                    })
                }

                tabs.push(
                    {
                        value: 'market_response_sheet',
                        text: 'Market Response Sheet',
                    },
                    {
                        value: 'documents',
                        text: 'Documents',
                    },
                );

                return tabs;
            },
            isCaptiveReinsurance() {
                return this.submission.transaction_type === 'captive_reinsurance';
            },
            captiveFirmOrderDataList() {
                return [
                    {
                        text: 'Line required',
                        value: this.$options.filters.currency(this.lineRequired, this.submission.currency.code)
                    },
                    {
                        text: 'Retained limit',
                        value: this.retainedLimit !== null ? this.$options.filters.currency(this.retainedLimit, this.submission.currency.code) : 'Pending'
                    },
                    {
                        text: 'Reinsured limit',
                        value: this.reinsuredLimit !== null ? this.$options.filters.currency(this.reinsuredLimit, this.submission.currency.code) : 'Pending'
                    },
                    {
                        text: 'Outstanding',
                        value: this.captiveOutstanding
                    },
                    {
                        text: 'Best Price',
                        value: `${this.bestPrice} BPS`
                    },
                    {
                        text: 'Waiting Period',
                        value: `${this.waitingPeriod} days`
                    }
                ]
            },
            insurerFirmOrderDataList() {
                if (this.isCaptiveReinsurance) {
                    return [
                        {
                            text: 'Reinsurance line required',
                            value: this.captiveInsurerOfferIsOffered ? this.$options.filters.currency(this.insurerLineRequired, this.submission.currency.code) : 'Pending'
                        },
                        {
                            text: 'Reinsurance line written',
                            value: this.$options.filters.currency(this.insurerLineWritten, this.submission.currency.code)
                        },
                        {
                            text: 'Outstanding',
                            value: this.captiveInsurerOfferIsOffered ? this.$options.filters.currency(this.outstanding, this.submission.currency.code) : 'Pending'
                        },
                        {
                            text: 'Best Price',
                            value: `${this.bestPrice} BPS`
                        },
                        {
                            text: 'Waiting Period',
                            value: `${this.waitingPeriod} days`
                        }
                    ]
                }

                return [
                    {
                        text: 'Line Required',
                        value: this.$options.filters.currency(this.lineRequired, this.submission.currency.code)
                    },
                    {
                        text: 'Line Written',
                        value: this.$options.filters.currency(this.lineWritten, this.submission.currency.code)
                    },
                    {
                        text: 'Outstanding',
                        value: this.$options.filters.currency(this.outstanding, this.submission.currency.code)
                    },
                    {
                        text: 'Best Price',
                        value: `${this.bestPrice} bps`
                    },
                    {
                        text: 'Waiting Period',
                        value: `${this.waitingPeriod} days`
                    }
                ]
            },
            hasObligor() {
                return this.submission.hasOwnProperty('product') && !this.submission.product.has_no_obligor;
            },
            waitingPeriod: function() {
                if (this.selectedOffers.length === 0) {
                    return '---';
                }

                return this.selectedOffers
                    .map(o => o.waiting_period)
                    .reduce(function(a, b) {
                        return Math.max(a, b);
                    }, 0);
            },
            limitAmount() {
                //for captive the limit amount is contract value
                if (this.isCaptiveReinsurance) {
                    return parseFloat(this.submission.contract_value);
                }

                return parseFloat(this.submission.limit_of_liability);
            },
            retainedLimit() {
                if (this.isCaptiveReinsurance && this.captiveInsurerOfferIsOffered) {
                    return parseFloat(this.captiveInsurerOffer.written_line_amount);
                }
                return null;
            },
            retainedPercent() {
                if (this.isCaptiveReinsurance && this.captiveInsurerOfferIsOffered) {
                    return parseFloat(this.captiveInsurerOffer.written_line_percent);
                }

                return 0;
            },
            reinsuredLimit() {
                if (this.retainedLimit !== null) {
                    return this.lineRequired - this.retainedLimit;
                }
                return null;
            },
            captiveInsurerOffer() {
                return this.submission.offers.find(o => Boolean(o.captive_insurer_company_id));
            },
            captiveInsurerOfferIsOffered() {
                if (this.captiveInsurerOffer) {
                    return this.captiveInsurerOffer.status === 'offered';
                }
            },
            retainedPercentOffer() {
                return this.selectedOffers.find(o => this.isRetainedPercentOffer(o.type));
            },
            isLineWrittenFull: function () {
                return new Big(this.percentageWritten).eq(100);
            },
            percentageRetained() {
                if (this.captiveInsurerOfferIsOffered) {
                    return parseFloat(this.captiveInsurerOffer.written_line_percent)
                }
            },
            insurerPercentageOffered() {
                if (!this.hasInsurerOfferedOffers) {
                    return 0;
                }

                let line = this.insurerOfferedOffers.map(function (offer) {
                    // check offered percent is a valid number, otherwise interpret as zero
                    if (offer.line_size_percent && !isNaN(offer.line_size_percent)) {
                        return parseFloat(offer.line_size_percent);
                    }

                    return 0;
                }).reduce((total, offeredLinePercent) => total + offeredLinePercent);

                if (this.isCaptiveReinsurance && this.captiveInsurerOfferIsOffered) {
                    return line / (100 - this.percentageRetained) * 100;
                }

                return line > 100 ? 100 : line;
            },
            lineRequired: function () {
                return this.limitAmount;
            },
            insurerLineRequired() {
                return this.lineRequired;
            },
            lineWritten: function () {
                return (this.percentageWritten / 100) * this.limitAmount;
            },
            insurerLineWritten() {
                return (this.insurerPercentageWritten / 100) * this.insurerLineRequired;
            },
            percentageOffered() {
                if (!this.hasOfferedOffers) {
                    return 0;
                }

                const line = this.offeredOffers.map(function (offer) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (offer.line_size_percent && !isNaN(offer.line_size_percent)) {
                        return parseFloat(offer.line_size_percent);
                    }

                    return 0;
                }).reduce((total, offeredLinePercent) => total + offeredLinePercent);

                return line;
            },
            percentageWritten() {
                if (!this.hasSelectedOffers) {
                    return 0;
                }

                const self = this;
                const line = this.selectedOffers.map(function (offer) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (offer.written_line_percent && !isNaN(offer.written_line_percent)) {
                        return self.round(offer.written_line_percent);
                    }

                    return 0;
                }).reduce((total, writtenLine) => total.plus(writtenLine), new Big(0));

                return self.round(line);
            },
            insurerPercentageWritten() {
                if (!this.hasSelectedOffers) {
                    return 0;
                }

                const self = this;
                const line = this.selectedInsurerOffers.map(function (offer) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (offer.adjusted_written_line_percent && !isNaN(offer.adjusted_written_line_percent)) {
                        return self.round(offer.adjusted_written_line_percent);
                    }

                    return 0;
                }).reduce((total, writtenLine) => total.plus(writtenLine), new Big(0));

                return self.round(line);
            },
            writtenColor() {
                return this.percentageWritten > 100 ? 'red' : 'yellow';
            },
            outstanding: function () {
                return this.insurerLineRequired - this.insurerLineWritten;
            },
            captiveOutstanding: function () {
                return this.insurerLineRequired - this.lineWritten;
            },
            bestPrice: function () {
                if (this.hasInsurerOfferedOffers === false) {
                    return 0;
                }

                return Math.min.apply(Math, this.insurerOfferedOffers.map(function (offer) {
                    return offer.price;
                }));
            },
            totalOffered: function () {
                if (this.submission.offers.length === 0 && this.hasOfferedOffers === false) {
                    return 0;
                }

                return this.offeredOffers.reduce((total, offer) => total + parseFloat(offer.line_size_percent), 0);
            },
            hasOfferedOffers: function () {
                return this.submission.offers.filter(offer => offer.status === 'offered').length > 0
            },
            hasInsurerOfferedOffers() {
                return this.submission.offers.filter(offer => offer.status === 'offered' && !offer.captive_insurer_company_id).length > 0
            },
            offeredOffers: function () {
                const self = this;

                if (this.hasOfferedOffers || this.isClosed) {
                    const offeredOffers = this.submission.offers.filter(function (offer) {
                        if (self.offeredOfferWasClosed(offer)) {
                            return true;
                        }
                        return ['offered', 'firm_ordered'].includes(offer.status);
                    });

                    offeredOffers.forEach(offer => {
                        if (this.isRetainedPercentOffer(offer.type)) {
                            offer.is_selected = true;
                            offer.written_line_percent = offer.line_size_percent;
                        }
                    });

                    return offeredOffers;
                }

                return [];
            },
            insurerOfferedOffers() {
                if (this.hasOfferedOffers) {
                    return this.offeredOffers.filter(function (offer) {
                        return !offer.captive_insurer_company_id;
                    });
                }
            },
            captiveOfferedOffer() {
                if (this.hasOfferedOffers) {
                    return this.offeredOffers.find(function (offer) {
                        return Boolean(offer.captive_insurer_company_id);
                    });
                }
            },
            insurerNonOfferedOffers() {
                return this.submission.offers.filter(function (offer) {
                    return offer.status !== 'offered' && !offer.captive_insurer_company_id;
                });
            },
            nonOfferedOffers: function () {
                const self = this;
                return this.submission.offers.filter(function (offer) {
                    return offer.status !== 'offered' && !self.offeredOfferWasClosed(offer);
                });
            },
            insurerList: function () {
                return this.submission.offers.map(offer => offer.insurer);
            },
            hasSelectedOffers: function () {
                return this.submission.offers.filter(offer => offer.is_selected === true).length > 0
            },
            selectedOffers: function () {
                if (this.hasSelectedOffers === false) {
                    return [];
                }

                return this.offeredOffers.filter(function (offer) {
                    return offer.is_selected === true;
                });
            },

            selectedInsurerOffers() {
                return this.selectedOffers.filter(function (offer) {
                    return !offer.captive_insurer_company_id;
                });
            },
            selectedOffersAreValid: function () {
                let valid = true;
                this.selectedOffers.forEach(offer => {
                    // if no percent selected (allowing 0 for retained_percent offers) or percent selected is greater than percent offered
                    if ((!offer.written_line_percent && !(this.isRetainedPercentOffer(offer.type) && offer.written_line_percent === 0)) || new Big(offer.written_line_percent).round(2, 0).cmp(new Big(offer.line_size_percent).round(2, 0)) === 1) {
                        valid = false;
                    }
                });
                return valid && this.securityLinesValid;
            },
            securityLinesValid() {
                // Every selected offer with securities on the risk needs a
                // security per line, distinct securities, and a multi-line
                // split whose percentages add up to the offer's written line.
                return this.selectedOffers.every(offer => {
                    if (this.getInsurerSecurities(offer).length === 0) {
                        return true;
                    }

                    const lines = this.offerLines(offer);

                    const securityIds = lines.map(line => line.security_id);
                    if (securityIds.some(id => !id)) {
                        return false;
                    }
                    if (new Set(securityIds).size !== securityIds.length) {
                        return false;
                    }

                    return lines.length === 1 || this.offerLinesSplitValid(offer);
                });
            },
            isInTheMarket: function () {
                return this.submission.status === 'in_the_market';
            },
            captiveInsuranceValid() {
                if (!this.isCaptiveReinsurance) {
                    return true;
                }

                return this.retainedPercentOffer && this.retainedPercentOffer.is_selected;
            },
            isClosed: function () {
                return this.submission.status === 'closed';
            },
            netMarginRequired() {
                return !this.submission.net_margin && this.selectedOffers.filter(o => o.type === 'percentage_of_net_margin').length > 0;
            },
            grossMarginRequired() {
                return !this.submission.gross_margin && this.selectedOffers.filter(o => o.type === 'percentage_of_gross_margin').length > 0;
            },
            netMarginNotProvided() {
                return this.netMarginRequired && !this.isFirmOrderValid;
            },
            grossMarginNotProvided() {
                return this.grossMarginRequired && !this.isFirmOrderValid;
            },
            percentageOfMarginOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedOffers.filter(o => ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(o.type)).length;
            },
            percentageOfNetMarginOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedOffers.filter(o => o.type === 'percentage_of_net_margin').length;
            },
            percentageOfGrossMarginOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedOffers.filter(o => o.type === 'percentage_of_gross_margin').length;
            },
            rateOffersOnly() {
                return this.selectedInsurerOffers.length === this.selectedOffers.filter(o => o.type === 'rate').length;
            },
            flatFeeOffersOnly() {
                return this.selectedOffers.length === this.selectedOffers.filter(o => o.type === 'flat_fee').length;
            },
            showEditBrokerage() {
                return this.$root.user.company.company_type === 'placing_broker' && this.editingBrokerage === false;
            },
            activeOffers() {
                return this.submission.offers.filter(function (offer) {
                    return offer.status !== 'ntu' && offer.status !== 'declined';
                });
            },
            nonNTUInsurersIds: function () {
                const nonNTUOffers = this.submission.offers.filter(offer => offer.status !== 'ntu');

                return nonNTUOffers.map(offer => offer.insurer.id);
            },
            filteredAddInsuresList() {
                return this.insurers.filter(insurer => !this.nonNTUInsurersIds.includes(insurer.id));
            },
            selectedInsurersList() {
                let selectedInsurers = Object.keys(this.selectedInsurersUsers).map(key => parseInt(key));
                return this.insurers.filter(insurer => selectedInsurers.includes(insurer.id));
            },
            canDownloadMarketSheet() {
                if (this.hasOfferedOffers) {
                    return true;
                }

                return this.nonOfferedOffers.filter(offer => offer.status !== 'requested').length > 0
            },
            // ================================================================================
            retainedEstimatedPremium() {
                let estimatedPremium = 0;

                if (this.captiveInsurerOfferIsOffered && this.captiveInsurerOffer.written_line_percent && this.pricing.horizontalPrice) {
                    estimatedPremium += parseFloat(premium.calculate(
                        this.lineRequired,
                        this.pricing.horizontalPrice,
                        this.submission.total_risk_period,
                        this.captiveInsurerOffer.written_line_percent
                    ));
                }

                return this.$options.filters.currency(estimatedPremium, this.submission.currency.code);
            },
            insurerEstimatedPremium: function () {
                if (this.selectedOffers.filter(o => o.type === 'percentage_of_net_margin').length > 0 && !this.submission.net_margin && !this.localPricing.net_margin) {
                    return 'Enter net margin';
                }
                if (this.selectedOffers.filter(o => o.type === 'percentage_of_gross_margin').length > 0 && !this.submission.gross_margin && !this.localPricing.gross_margin) {
                    return 'Enter gross margin';
                }

                let estimatedPremium = 0;

                this.selectedOffers.filter(offer => Boolean(offer.written_line_percent)).forEach(offer => {
                    const price = this.pricing.structure === 'horizontal' ? this.pricing.horizontalPrice : offer.price;

                    if (offer.type === 'flat_fee') {
                        estimatedPremium += offer.flat_fee;
                    }

                    if (offer.type === 'rate') {
                        estimatedPremium += parseFloat(premium.calculate(
                            this.lineRequired,
                            price,
                            this.submission.total_risk_period,
                            offer.written_line_percent
                        ));
                    }

                    if (['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(offer.type)) {
                        if (this.localPricing.structure === 'vertical') {
                            const margin = this.getMarginByType(offer.type);
                            const percentageOfMarginPrice = Math.max(Math.ceil((offer.percentage_of_margin / 100) * margin), offer.minimum_price);

                            estimatedPremium += parseFloat(premium.calculate(
                                this.lineRequired,
                                percentageOfMarginPrice,
                                this.submission.total_risk_period,
                                offer.written_line_percent
                            ));
                        } else {
                            estimatedPremium += parseFloat(premium.calculate(
                                this.lineRequired,
                                this.localPricing.horizontalPrice,
                                this.submission.total_risk_period,
                                offer.written_line_percent
                            ));
                        }
                    }
                });

                return this.$options.filters.currency(estimatedPremium, this.submission.currency.code)
            },
            estimatedPremium: function () {
                if (this.selectedOffers.filter(o => o.type === 'percentage_of_net_margin').length > 0 && this.submission.net_margin === null && this.localPricing.net_margin === null) {
                    return 'Enter net margin';
                }
                if (this.selectedOffers.filter(o => o.type === 'percentage_of_gross_margin').length > 0 && this.submission.gross_margin === null && this.localPricing.gross_margin === null) {
                    return 'Enter gross margin';
                }

                let estimatedPremium = 0;

                this.selectedOffers.filter(offer => typeof offer.written_line_percent === 'number').forEach(offer => {
                    const price = this.pricing.structure === 'horizontal' ? this.pricing.horizontalPrice : offer.price;

                    if (offer.type === 'flat_fee') {
                        estimatedPremium += offer.flat_fee;
                    }

                    if (offer.type === 'rate') {
                        estimatedPremium += parseFloat(premium.calculate(
                            this.lineRequired,
                            price,
                            this.submission.total_risk_period,
                            offer.written_line_percent
                        ));
                    }

                    if (['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(offer.type)) {
                        if (this.localPricing.structure === 'vertical') {
                            const margin = this.getMarginByType(offer.type);
                            const percentageOfMarginPrice = Math.max((offer.percentage_of_margin / 100) * margin, offer.minimum_price).toFixed(2);

                            estimatedPremium += parseFloat(premium.calculate(
                                this.lineRequired,
                                percentageOfMarginPrice,
                                this.submission.total_risk_period,
                                offer.written_line_percent
                            ));
                        } else {
                            estimatedPremium += parseFloat(premium.calculate(
                                this.lineRequired,
                                this.localPricing.horizontalPrice,
                                this.submission.total_risk_period,
                                offer.written_line_percent
                            ));
                        }
                    }
                });

                return this.$options.filters.currency(estimatedPremium, this.submission.currency.code);
            },
        },
        watch: {
            async percentageWritten(newPercentageWritten) {
                if (this.percentagesNeedFixing(newPercentageWritten)) {
                    await this.$nextTick(); // Fixes issue with written_line_amount being null on some offers.
                    this.fixPercentages(this.selectedOffers, 'written_line_percent');
                }
            },
            async insurerPercentageWritten(newPercentageWritten) {
                if (this.percentagesNeedFixing(newPercentageWritten)) {
                   await this.$nextTick(); // Fixes issue with written_line_amount being null on some offers.
                    this.fixPercentages(this.selectedInsurerOffers, 'adjusted_written_line_percent');
                 }
            },
            actionMenuItems: {
                handler(newItems) {
                    this.$eventBus.$emit('update-action-menu-items', newItems);
                },
                immediate: true
            },
            'submission.offers': {
                handler() {
                    const self = this;
                    let limit = this.limitAmount;

                    this.calculateHorizontalPrice();
                    this.$emit('update:pricing', this.localPricing);

                    this.selectedOffers.forEach(function (offer) {

                        if (offer.adjusted_written_line_percent) {
                            offer.written_line_percent = offer.adjusted_written_line_percent;
                            offer.written_line_amount = (offer.written_line_percent / 100) * limit;
                        } else if (offer.written_line_percent) {
                            offer.written_line_amount = (offer.written_line_percent / 100) * limit;
                        } else {
                            offer.written_line_amount = 0;
                            offer.written_line_percent = 0;
                        }

                        const insurerSecurities = self.getInsurerSecurities(offer);
                        if (insurerSecurities.length === 1) {
                            offer.security_id = insurerSecurities[0].id;

                            if (offer.lines && offer.lines.length === 1) {
                                offer.lines[0].security_id = insurerSecurities[0].id;
                            }
                        }
                    });
                },
                deep: true
            },
            selectedOffers: {
                // Seed each selected offer's security lines outside the render path.
                handler(offers) {
                    (offers || []).forEach(offer => this.getOfferLines(offer));
                },
                immediate: true
            },
        },
        created() {
            if (this.draftFirmOrder) {
                this.prefillFirmOrderWithDraft();
            }

            this.$eventBus.$on('action-edit-risk', () => this.goToEditFirmOrder());
            this.$eventBus.$on('action-close-risk', () => this.closeSubmission());
            this.$eventBus.$on('action-reopen-risk', () => this.reopenSubmission());
            this.$eventBus.$on('action-create-a-copy', () => this.creatACopy());
        },
        async mounted() {
            await this.getInsurers();
            this.loading = false;
        },
        beforeDestroy() {
            this.$eventBus.$off('action-edit-risk');
            this.$eventBus.$off('action-close-risk');
            this.$eventBus.$off('action-reopen-risk');
            this.$eventBus.$off('action-create-a-copy');
        },
        methods: {
            getInsurerSecurities(offer) {
                if (offer.securities.length > 0) {
                    return offer.securities;
                }

                return this.submission?.facility?.securities?.filter(security => security.insurer_company_id === offer.insurer_company_id) || [];
            },
            getSecuritiesFormatted(securities) {
                return securities.map(security => ({
                    text: security.name,
                    value: security.id
                }));
            },
            offerLines(offer) {
                // Non-mutating read for the render path; the selectedOffers
                // watcher seeds offer.lines before any row becomes editable.
                if (offer.lines && offer.lines.length > 0) {
                    return offer.lines;
                }

                return [
                    {
                        security_id: offer.security_id || null,
                        written_line_percent: offer.adjusted_written_line_percent || offer.written_line_percent,
                        insurer_role: offer.insurer_role || null
                    }
                ];
            },
            getOfferLines(offer) {
                if (!offer.lines || offer.lines.length === 0) {
                    this.$set(offer, 'lines', this.offerLines(offer));
                }

                return offer.lines;
            },
            lineSecurityOptions(offer, lineIndex) {
                const siblingSecurityIds = this.offerLines(offer)
                    .filter((line, index) => index !== lineIndex)
                    .map(line => line.security_id)
                    .filter(Boolean);

                return this.getInsurerSecurities(offer)
                    .filter(security => !siblingSecurityIds.includes(security.id));
            },
            canAddSecurityLine(offer) {
                // A zero-percent written line cannot be split proportionally.
                return !this.isCaptiveReinsurance &&
                    (parseFloat(offer.adjusted_written_line_percent) || 0) > 0 &&
                    this.getInsurerSecurities(offer).length > this.offerLines(offer).length;
            },
            lineFlatFee(offer, line) {
                const lines = this.offerLines(offer);

                if (lines.length === 1) {
                    return offer.flat_fee;
                }

                const totalPercent = lines.reduce((sum, l) => sum + (parseFloat(l.written_line_percent) || 0), 0);

                if (!totalPercent) {
                    return 0;
                }

                return (parseFloat(offer.flat_fee) || 0) * ((parseFloat(line.written_line_percent) || 0) / totalPercent);
            },
            addSecurityLine(offer) {
                this.getOfferLines(offer).push({
                    security_id: null,
                    written_line_percent: 0,
                    insurer_role: null
                });
            },
            removeSecurityLine(offer, lineIndex) {
                const lines = this.getOfferLines(offer);

                lines.splice(lineIndex, 1);

                if (lines.length === 1) {
                    lines[0].written_line_percent = offer.adjusted_written_line_percent || offer.written_line_percent;
                }

                offer.security_id = lines[0].security_id;
                offer.insurer_role = lines[0].insurer_role || null;
            },
            setLineSecurity(offer, lineIndex, value) {
                this.getOfferLines(offer)[lineIndex].security_id = value;

                if (lineIndex === 0) {
                    offer.security_id = value;
                }
            },
            setLineWrittenPercent(offer, lineIndex, value) {
                this.getOfferLines(offer)[lineIndex].written_line_percent = parseFloat(value) || 0;
            },
            lineWrittenPercentRule(value) {
                const percent = parseFloat(value);

                if (Number.isNaN(percent) || percent <= 0) {
                    return 'Written line percentage must be above zero.';
                }

                return true;
            },
            offerLinesSplitValid(offer) {
                const lines = this.offerLines(offer);

                if (lines.some(line => (parseFloat(line.written_line_percent) || 0) <= 0)) {
                    return false;
                }

                const total = lines.reduce((sum, line) => sum + (parseFloat(line.written_line_percent) || 0), 0);
                const target = parseFloat(offer.adjusted_written_line_percent || offer.written_line_percent) || 0;

                return Math.abs(total - target) < 1e-9;
            },
            percentageRemaining(offers, column) {
                const self = this;
                const line = offers.map(function (offer) {
                    // check written percent is a valid number, otherwise interpret as zero
                    if (offer[column] && !isNaN(offer[column])) {
                        return self.round(offer[column]);
                    }

                    return 0;
                }).reduce((total, writtenLine) => total + writtenLine, 0);

                return line - 100;
            },
            getPercentageOfMarginOffer (offer) {
                if (this.localPricing.net_margin !== null && offer.type === 'percentage_of_net_margin') {
                    offer.price = Math.max(this.localPricing.net_margin * (offer.percentage_of_margin / 100), offer.minimum_price).toFixed(2);
                    return this.$options.filters.numberNoTrail(offer.price) + ' BPS';
                } else if (this.submission.net_margin === null && offer.type === 'percentage_of_net_margin') {
                    return this.$options.filters.numberNoTrail(offer.price) + ' BPS';
                } else if (this.localPricing.gross_margin !== null && offer.type === 'percentage_of_gross_margin') {
                    offer.price = Math.max(this.localPricing.gross_margin * (offer.percentage_of_margin / 100), offer.minimum_price).toFixed(2);
                    return this.$options.filters.numberNoTrail(offer.price) + ' BPS';
                } else if (this.submission.gross_margin === null && offer.type === 'percentage_of_gross_margin') {
                    return this.$options.filters.numberNoTrail(offer.price) + ' BPS';
                }

                return `${offer.percentage_of_margin}%  (Min: ${offer.minimum_price} BPS)`;
            },
            async goToNextStep() {
                this.$emit('firm-order:navigate-to', this.currentStep + 1)
            },
            prefillFirmOrderWithDraft() {
                this.offeredOffers.forEach((offer) => {
                    const draftOffers = this.draftFirmOrder.firm_order_offers.filter(draftOffer => draftOffer.offer_id === offer.id);
                    if (draftOffers.length > 0) {
                        offer.is_selected = true;
                        offer.adjusted_written_line_percent = draftOffers.reduce((sum, draftOffer) => sum + (draftOffer.written_line_percent || 0), 0);
                        offer.security_id = draftOffers[0].security_id;
                        this.$set(offer, 'lines', draftOffers.map(draftOffer => ({
                            security_id: draftOffer.security_id,
                            written_line_percent: draftOffer.written_line_percent,
                            insurer_role: draftOffer.insurer_role || null
                        })));
                        offer.insurer_role = draftOffers[0].insurer_role || null;
                    }
                });

                if (this.draftFirmOrder.net_margin !== null && !this.submission.net_margin) {
                    this.localPricing.net_margin = parseFloat(new Big(this.draftFirmOrder.net_margin));
                }
                if (this.draftFirmOrder.gross_margin !== null && !this.submission.gross_margin) {
                    this.localPricing.gross_margin = parseFloat(new Big(this.draftFirmOrder.gross_margin));
                }
                if (this.draftFirmOrder.status === 'draft') {
                    this.submission.entity_id = this.draftFirmOrder.entity_id;
                }
                this.localPricing.structure = this.draftFirmOrder.pricing_structure;
            },
            switched() {
                if (this.localPricing.structure === 'horizontal') {
                    this.selectedOffers.filter(o => o.type === 'flat_fee').forEach(function (offer) {
                        offer.is_selected = false;
                    });

                    this.deselectOffersThatAreNotTheSame();
                }

                this.calculateHorizontalPrice();
                this.$emit('update:pricing', this.localPricing);
            },
            deselectOffersThatAreNotTheSame() {
                const firstOfferType = this.selectedOffers[0].type;
                this.selectedOffers.filter(o => o.type !== firstOfferType).forEach((offer) => {
                    offer.is_selected = false;
                })
            },
            async signDownFirmOrder() {
                if (await this.$refs.signDown.open() === false) {
                    return;
                }

                let totalPercentLeftForOffers = 100;
                if (this.isCaptiveReinsurance) {
                    totalPercentLeftForOffers -= this.captiveInsurerOffer.written_line_percent
                }

                this.selectedInsurerOffers.forEach((selectedOffer) => {
                    const linePercent = parseFloat(selectedOffer.adjusted_line_size_percent);
                    const totalPercentSelected = parseFloat(this.totalInsurerSelectedPercentageOffered());
                    const signedDownPercentString = (linePercent * (totalPercentLeftForOffers / totalPercentSelected)).toFixed(12);
                    selectedOffer.adjusted_written_line_percent = parseFloat(signedDownPercentString);
                });

                // use setTimeout so the submission.offers watcher can apply written_line_percent from the adjusted_written_line_percent before executing below
                setTimeout(() => {
                    if (this.totalWrittenPercentageForSelectedOffers() !== 100) {
                        const highestOffers = this.selectedInsurerOffers.filter((offer) => {
                            // offer(s) with highest written line amount
                            return offer.written_line_percent === this.highestSelectedInsurerOfferPercent();
                        });
                        const numberOfHighestOffers = highestOffers.length;

                        if (this.signDownRemainderCanBeSplitEqually(numberOfHighestOffers)) {
                            const totalWrittenPercent = new Big(this.totalWrittenPercentageForSelectedOffers());
                            const percentageToAdd = new Big(100).minus(totalWrittenPercent).div(numberOfHighestOffers);

                            highestOffers.forEach(function (offer) {
                                offer.adjusted_written_line_percent += Number(percentageToAdd);
                            });
                        } else {
                            const totalWrittenPercent = new Big(this.totalWrittenPercentageForSelectedOffers());
                            const randomHighestOffer = new Big(highestOffers[0].adjusted_written_line_percent);
                            const percentageToAdd = new Big(100).minus(totalWrittenPercent);

                            highestOffers[0].adjusted_written_line_percent = randomHighestOffer.plus(percentageToAdd).toFixed(12);
                        }
                    }
                }, 0);
            },
            goToEditFirmOrder() {
                this.$router.push({path: `/agent/firm-order/${this.$route.params.id}/edit`});
            },
            signDownRemainderCanBeSplitEqually(highestOffersCount) {
                // we use the difference * 1e+12 to convert the 12 decimal number to an integer, to see if it can be split evenly between the number of offers
                return new Big(100).minus(this.totalWrittenPercentageForSelectedOffers()).times(1e+12).mod(highestOffersCount).eq(0);
            },
            totalSelectedPercentageOffered() {
                return this.selectedOffers.reduce((total, offer) => total + parseFloat(offer.line_size_percent), 0);
            },
            totalInsurerSelectedPercentageOffered() {
                return this.selectedInsurerOffers.reduce((total, offer) => total + parseFloat(offer.adjusted_line_size_percent), 0);
            },
            totalWrittenPercentageForSelectedOffers() {
                return parseFloat(this.selectedOffers.reduce((total, offer) => total.plus(offer.written_line_percent), new Big(0)));
            },
            highestSelectedInsurerOfferPercent() {
                return Math.max.apply(Math, this.selectedInsurerOffers.map(function (selectedOffer) {
                    return parseFloat(selectedOffer.written_line_percent);
                }));
            },
            calculateHorizontalPrice() {
                if (this.selectedInsurerOffers.length === 0) {
                    this.localPricing.horizontalPercentageOfMargin = false;
                    this.localPricing.horizontalPrice = false;
                    this.$emit('update:pricing', this.localPricing);
                } else if (this.percentageOfNetMarginOffersOnly) {
                    const net_margin = this.localPricing.net_margin !== null ? this.localPricing.net_margin : this.submission.net_margin;
                    const highestMinimumPrice = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_net_margin')
                            .map(o => o.minimum_price)
                    );
                    const highestPercentageOfMargin = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_net_margin')
                            .map(o => o.percentage_of_margin));

                    this.localPricing.horizontalPercentageOfMargin = highestPercentageOfMargin;

                    this.localPricing.horizontalPrice = Math.max(net_margin * (highestPercentageOfMargin / 100), highestMinimumPrice).toFixed(2);
                    this.$emit('update:pricing', this.localPricing);
                } else if (this.percentageOfGrossMarginOffersOnly) {
                    const gross_margin = this.localPricing.gross_margin !== null ? this.localPricing.gross_margin : this.submission.gross_margin;
                    const highestMinimumPrice = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_gross_margin')
                            .map(o => o.minimum_price)
                    );
                    const highestPercentageOfMargin = Math.max(
                        ...this.selectedOffers
                            .filter(o => o.type === 'percentage_of_gross_margin')
                            .map(o => o.percentage_of_margin));

                    this.localPricing.horizontalPercentageOfMargin = highestPercentageOfMargin;

                    this.localPricing.horizontalPrice = Math.max(gross_margin * (highestPercentageOfMargin / 100), highestMinimumPrice).toFixed(2);
                    this.$emit('update:pricing', this.localPricing);
                } else if (this.rateOffersOnly) {
                    const highestPrice = Math.max(...this.selectedInsurerOffers.map(o => o.price));

                    this.localPricing.horizontalPrice = highestPrice;
                    this.$emit('update:pricing', this.localPricing);
                }
            },
            showHorizontalPriceChange(originalPrice) {
                return this.rateOffersOnly && this.pricing.structure === 'horizontal' && this.pricing.horizontalPrice > originalPrice;
            },
            showHorizontalPercentageChange(offer, isRetainedOffer) {
                if (this.netMarginRequired && this.netMarginNotProvided || (this.grossMarginRequired && this.grossMarginNotProvided)) {
                    return false;
                }

                if (isRetainedOffer) {
                    return this.percentageOfMarginOffersOnly && this.pricing.horizontalPercentageOfMargin;
                }

                return this.percentageOfMarginOffersOnly && this.pricing.structure === 'horizontal' && this.pricing.horizontalPrice > parseFloat(this.getPercentageOfMarginOffer(offer));
            },
            getOfferComment(offer) {
                let comment = '';

                if (offer.status === 'declined' || Boolean(offer.decline_reason_id)) {
                    comment = `Decline reason: ${offer.decline_reason}`;
                } else if (offer.status === 'ntu' && offer.decline_reason) {
                    comment = `Removal reason: ${offer.decline_reason}`;
                } else if (offer.query) {
                    comment = `Query: ${offer.query}`;
                }

                return comment;
            },
            async closeSubmission() {
                if (await this.$refs.closeSubmission.open() === false) {
                    return;
                }

                this.closing = true;

                await axios.put(this.$root.api(`broker/submission/${this.$route.params.id}/close`)).then(() => {
                    this.goToDashboard();
                }).then(() => {
                    this.closing = false;
                });
            },
            creatACopy() {
                this.$router.push(
                    {
                        name: 'broker-new-risk',
                        params: {
                            submissionCopy: this.submission
                        }
                    }
                );
            },
            async reopenSubmission() {
                if (await this.$refs.reopenSubmission.open() === false) {
                    return;
                }

                this.reopening = true;

                await axios.put(this.$root.api(`broker/submission/${this.$route.params.id}/reopen`)).then(() => {
                    this.$eventBus.$emit('submissionChanged');
                }).then(() => {
                    this.reopening = false;
                });
            },
            goToDashboard() {
                this.$router.push({name: 'broker-dashboard'});
            },
            isRateOffer(type) {
                return type === 'rate';
            },
            isFlatFeeOffer(type) {
                return type === 'flat_fee';
            },
            isPercentageOfMarginOffer(type) {
                return ['percentage_of_net_margin', 'percentage_of_gross_margin'].includes(type);
            },
            isRetainedPercentOffer(type) {
                return type === 'retained_percent';
            },
            offerIsDisabled(offer) {
                if (!this.isCaptiveReinsurance || this.selectedInsurerOffers.length === 0) {
                    return false;
                }

                if (this.rateOffersOnly && !this.isRateOffer(offer.type)) {
                    return true;
                }

                if (this.percentageOfMarginOffersOnly && !this.isPercentageOfMarginOffer(offer.type)) {
                    return true;
                }

                return false;
            },
            checkInclude(offer) {
                offer.is_selected = !offer.is_selected;

                if (this.isCaptiveReinsurance) {
                    return;
                }

                if (!this.rateOffersOnly && !this.percentageOfNetMarginOffersOnly && !this.percentageOfGrossMarginOffersOnly && !this.flatFeeOffersOnly) {
                    this.localPricing.structure = 'vertical';
                    this.$emit('update:pricing', this.localPricing);
                }

                if (offer.type === 'flat_fee') {
                    this.localPricing.structure = 'vertical';
                    this.$emit('update:pricing', this.localPricing);
                }
            },
            getOfferTypeName(type) {
                switch (type) {
                    case 'rate':
                        return 'Rate';
                    case 'flat_fee':
                        return 'Flat fee';
                    case 'percentage_of_net_margin':
                        return 'Percentage of net margin';
                    case 'percentage_of_gross_margin':
                        return 'Percentage of gross margin';
                    case 'retained_percent':
                        return 'Retained Percent';
                }
            },
            async editBrokerage(offer) {
                this.editingBrokerage = true;
                this.brokerage = offer.brokerage;

                if (await this.$refs.editBrokerage.open() === true) {
                    const updatedOffer = (await axios.put(this.$root.api(`broker/submission/${offer.submission_id}/offer/${offer.id}`), { brokerage: this.brokerage })).data.data;
                    this.$eventBus.$emit('brokerageChanged', updatedOffer);
                }
                this.editingBrokerage = false;
            },
            // Check if percentage is very close to 100% so that we can attempt to update
            // the percentages to make the firm order go through.
            // TODO this logic is duplicated in BrokerAmendmentEntry.vue, could be abstracted
            percentagesNeedFixing(newPercentageWritten) {
                const autoFixRange = 0.001;

                return newPercentageWritten !== 100 &&
                    newPercentageWritten > (100 - autoFixRange) &&
                    newPercentageWritten < (100 + autoFixRange);
            },
            // Take the small amount of percent that's either under or over and attempt to add/subtract that between the
            // selected offers without changing the currency value.
            // TODO this logic is duplicated in BrokerAmendmentEntry.vue, could be abstracted
            fixPercentages(selectedOffers, column) {
                const self = this;
                const limit = this.limitAmount;
                let remainingPercent = this.percentageRemaining(selectedOffers, column);

                for (let offer of selectedOffers) {
                    if (remainingPercent === 0) {
                        break;
                    }

                    const maxLinePercent = Math.min(offer.line_size_percent, self.round(((offer.written_line_amount / limit) * 100)));

                    let changeAmount = 0;
                    if (remainingPercent < 0) {
                        changeAmount = Math.min(remainingPercent, maxLinePercent - offer[column]);
                    } else if (remainingPercent > 0) {
                        changeAmount = Math.max(remainingPercent, maxLinePercent - offer[column]);
                    }

                    offer[column] -= changeAmount;
                    remainingPercent = this.percentageRemaining(selectedOffers, column);
                }
            },
            offeredOfferWasClosed(offer) {
                return this.isClosed && Boolean(offer.type) && Boolean(offer.line_size_percent);
            },
            offerWasDeclined(offer) {
                return this.isClosed && Boolean(offer.decline_reason_id) && Boolean(offer.decline_reason);
            },
            async getInsurers() {
                if (this.submission.facility_id !== null) {
                    this.insurers = (await axios.get(this.$root.api(`insurer/facility/${this.submission.facility_id}`), {responseType: 'json'})).data.data;
                } else {
                    this.insurers = (await axios.get(this.$root.api('insurer'), {responseType: 'json'})).data.data;
                }
            },
            isValidRemove(offer) {
                if (!this.isInTheMarket || offer.status === 'ntu' || offer.status === 'declined' || this.activeOffers.length < 2) {
                    return false;
                }

                return true;
            },
            async openAddInsurersModal() {
                if (await this.$refs.addInsurersModal.open() === false) {
                    return;
                }

                if (Object.keys(this.selectedInsurersUsers).length > 0) {

                    if (await this.$refs.addInsurersConfirmModal.open() === false) {
                        this.selectedInsurersUsers = {};
                        return;
                    }

                    this.loading = true;
                    axios.post(
                        this.$root.api('broker/submission/') + this.submission.id + '/underwriter', {
                            insurersUsers: this.selectedInsurersUsers
                        }
                    ).then(() => {
                        this.$eventBus.$emit('brokerSubmissionChanged');
                    });
                }
            },
            removeOffer(offer) {
                this.removingOffer = offer;
                this.showRemoveInsurerDialog = true;
            },
            async removeOfferForm() {
                this.removeInsurerLoading = true;

                if (await this.$refs.confirmRemoveOffer.open() === false) {
                    this.removeInsurerLoading = false;
                    return;
                }

                this.reopening = true;

                await axios.put(this.$root.api(`broker/submission/${this.$route.params.id}/offer/${this.removingOffer.id}/ntu`), {
                    reason: this.removeInsurerReason
                }).then(() => {
                    this.$eventBus.$emit('brokerSubmissionChanged');
                }).then(() => {
                    this.removeInsurerReason = null;
                    this.showRemoveInsurerDialog = false;
                    this.removeInsurerLoading = false;
                    this.reopening = false;
                });

                this.removingOffer = null;
            },
            round(value, decimalPlaces = this.percentageDecimalPlaces) {
                value = parseFloat(value);

                if (parseFloat(value.toFixed(decimalPlaces)) !== value && !isNaN(value)) {
                    value = value.toFixed(decimalPlaces);
                }

                return Number(value);
            },
            isFacilityActive() {
                return this.isStandalonePolicy || this.submission.facility?.status === 1;
            },
            getEndLabelColor(percentage) {
                if (percentage === 100) {
                    return 'secondary';
                } else if (percentage > 100) {
                    return 'error'
                }

                return 'amber';
            },
            getMarginByType(type) {
                switch (type) {
                    case 'percentage_of_net_margin':
                        return this.submission.net_margin ? this.submission.net_margin : this.localPricing.net_margin;
                    case 'percentage_of_gross_margin':
                        return this.submission.gross_margin ? this.submission.gross_margin : this.localPricing.gross_margin;
                    default:
                        return null;
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
.security-line-error {
    color: $color-error;
}

.security-line-percent {
    :deep(input[type="number"]) {
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
    }
}

.captive-offer-table,
.captive-requested-offer-table,
.offered-offers-table,
.requested-offers-table {
    .included-column {
        .x-input {
            justify-content: center;

            :deep(.x-input__slot) {
                margin: 0;

                .x-input--selection-controls__input {
                    width: 24px;
                    height: 24px;
                    margin: 0;

                    .x-input--selection-controls__check {
                        width: 24px;
                        height: 24px;

                        i {
                            font-size: rem(14px);
                        }
                    }
                }
            }

        }
    }

    .insurer-column {
        .insurer-name {
            font-weight: 600;
        }

        i {
            cursor: pointer;
            color: $color-mustard;
            font-size: rem(12px);
            padding: 6px;
        }
    }

    .progress-linear-bar :deep() {
        .label {
            font-family: $font-body;
            margin-bottom: 8px;

            .primary-label {
                font-weight: 600;
            }
        }
    }
}

.captive-offer-table,
.captive-requested-offer-table {
    :deep(thead) tr th:last-child {
        visibility: hidden;
    }
}

.sign-down-button {
    margin: 0;
}

.subjectivity-text {
    color: black;
}

.sub-text {
    font-size: 0.75rem;
}
</style>
